import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PhoneSheet } from "../components/phone-sheet";
import { Button } from "../components/ui/button";
import { Centered, Screen, Sheet } from "../components/ui/layout";
import { Icon } from "../components/ui/icons";
import { Spinner } from "../components/ui/spinner";
import { errorMessage, isDeadSessionError } from "../libs/errors";
import { pickItems, type PickResult, type PickScope } from "../modules/pick/api";
import { getScans, sendScan, type ActivityScans } from "../modules/scans/api";
import {
  closeShift,
  enterState,
  finishState,
  getConfig,
  getShift,
  openShift,
  type ShiftStatus,
  type StaffConfig,
} from "../modules/states/api";
import {
  answersBody,
  answersSummary,
  outcomeItems,
  type DraftResult,
  type OutcomeItem,
  type QuestionnaireAnswers,
} from "../modules/states/tree";

import { type Demand, type DetailsPrefill, DetailsScreen, DocumentsScreen, OutcomesScreen } from "./outcomes";
import { PickResultScreen, PickScreen } from "./pick";
import { ClosedScreen, HallScreen, InStateScreen } from "./screens";
import { StatsScreen } from "./StatsScreen";

// Сканы консультации живут в 1С и меняются, пока продавец на главной приложения сканирует бирки:
// перечитываем их при возврате на экран и по таймеру.
const SCANS_REFRESH_MS = 20_000;

// Экран, открытый поверх текущего состояния смены. Само состояние (закрыта / в базовом / в другом)
// приходит с бэкенда; view — только то, что сотрудник сейчас заполняет.
export type View =
  | { name: "main" }
  | { name: "outcomes" }
  | { name: "documents"; outcome: OutcomeItem }
  | { name: "details"; outcome: OutcomeItem; prefill?: DetailsPrefill }
  | { name: "stats" }
  | { name: "pick" }
  | { name: "pickResult"; scope: PickScope; result: PickResult };

// Шторка поверх экрана консультации: ввод телефона, либо подсказки при входе по настройкам мерчанта.
type Prompt = "phone" | "customer" | "fitting" | null;

export function ShiftApp() {
  const [config, setConfig] = useState<StaffConfig | null>(null);
  const [status, setStatus] = useState<ShiftStatus | null>(null);
  const [view, setView] = useState<View>({ name: "main" });
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  // Отмеченные, но ещё не отправленные результаты текущей консультации.
  const [drafts, setDrafts] = useState<DraftResult[]>([]);
  const [scans, setScans] = useState<ActivityScans | null>(null);
  const [prompt, setPrompt] = useState<Prompt>(null);
  // Подбор: ответы анкеты живут, пока продавец не ушёл из состояния; записанный запрос клиента — до финиша.
  const [pickAnswers, setPickAnswers] = useState<QuestionnaireAnswers>({});
  const [pickScope, setPickScope] = useState<PickScope>("shop");
  const [demand, setDemand] = useState<Demand | null>(null);

  const load = useCallback(async () => {
    try {
      const [nextConfig, nextStatus] = await Promise.all([getConfig(), getShift()]);
      setConfig(nextConfig);
      setStatus(nextStatus);
      setFailed(false);
      setError(null);
    } catch (e) {
      if (isDeadSessionError(e)) setFailed(true);
      else setError(errorMessage(e));
    }
  }, []);

  useEffect(() => void load(), [load]);

  // Тикаем, пока идёт смена: таймер текущего состояния и длительность смены.
  useEffect(() => {
    if (!status?.shift) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [status?.shift]);

  const states = useMemo(() => [...(config?.states ?? [])].sort((a, b) => a.sort - b.sort), [config]);
  const currentState = states.find((state) => state.id === status?.current?.stateId) ?? null;
  const hasOutcomes = outcomeItems(currentState?.outcomes ?? []).length > 0;
  // Консультация — небазовое состояние с исходами: только в ней есть клиент и сканы.
  const inConsultation = Boolean(status?.current && !status.current.isBase && hasOutcomes);

  // Новое пребывание в состоянии — новая консультация: черновик результатов и сканы с чистого листа.
  const activityId = status?.current?.activityId;
  useEffect(() => {
    setDrafts([]);
    setScans(null);
    setPickAnswers({});
    setPickScope("shop");
    setDemand(null);
  }, [activityId]);

  // Один запрос сканов за раз: возврат на вкладку и таймер могут совпасть.
  const scansBusy = useRef(false);
  const refreshScans = useCallback(async () => {
    if (scansBusy.current) return;
    scansBusy.current = true;
    try {
      setScans(await getScans());
    } catch {
      // 1С не ответила — блок клиента и изделий просто не показываем, остальное работает.
    } finally {
      scansBusy.current = false;
    }
  }, []);

  useEffect(() => {
    if (!inConsultation) return;
    void refreshScans();
    const timer = setInterval(() => void refreshScans(), SCANS_REFRESH_MS);
    const onVisible = () => document.visibilityState === "visible" && void refreshScans();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [inConsultation, activityId, refreshScans]);

  const run = useCallback(
    async (action: () => Promise<ShiftStatus | void>, next: View = { name: "main" }): Promise<ShiftStatus | null> => {
      setBusy(true);
      setError(null);
      try {
        const result = (await action()) ?? (await getShift());
        setStatus(result);
        setView(next);
        return result;
      } catch (e) {
        if (isDeadSessionError(e)) setFailed(true);
        else setError(errorMessage(e));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  if (failed) {
    return (
      <Screen>
        <Centered>
          <span className="mb-2 grid size-16 place-items-center rounded-full bg-black/5 text-[var(--color-muted)]">
            <Icon name="phone" className="size-8" />
          </span>
          <p className="max-w-72 text-center text-lg text-[var(--color-muted)]">
            Откройте раздел «Интерфейс продавца» из мобильного приложения.
          </p>
        </Centered>
      </Screen>
    );
  }

  if (!config || !status) {
    return (
      <Screen>
        <Centered>
          {error ? (
            <>
              <p className="max-w-72 text-center text-[var(--color-muted)]">
                Не удалось загрузить смену. Проверьте интернет и попробуйте ещё раз.
              </p>
              <Button variant="secondary" className="w-auto" onClick={() => void load()}>
                Повторить
              </Button>
            </>
          ) : (
            <Spinner className="size-8 text-[var(--color-accent)]" />
          )}
        </Centered>
      </Screen>
    );
  }

  // «Завершить»: все отмеченные результаты и, если есть, тот, что заполнен на экране, — одним запросом.
  const finish = (result?: DraftResult) =>
    run(() => finishState({ results: [...drafts, ...(result ? [result] : [])].map((draft) => draft.body) }));
  // «Ещё результат»: запомнить и вернуться к выбору исхода.
  const addDraft = (result: DraftResult) => {
    setDrafts((current) => [...current, result]);
    setView({ name: "outcomes" });
  };
  // Вход в состояние; в консультацию — с подсказкой, если мерчант так настроил.
  const enter = async (stateId: string) => {
    const result = await run(() => enterState(stateId));
    const state = states.find((item) => item.id === stateId);
    if (!result?.current || result.current.isBase || !state || !outcomeItems(state.outcomes).length) return;
    if (config.askCustomerOnEnter) setPrompt("customer");
    else if (config.askFittingOnEnter) setPrompt("fitting");
  };
  const draftProps = { drafts, onAdd: addDraft, onFinish: finish };
  const common = { busy, error, now };

  // Анкета с подбором — та же, что при отказе; нет её в справочнике — кнопок «Подобрать» нет.
  const pickQuestionnaire = config.questionnaires?.find((questionnaire) => questionnaire.pick) ?? null;
  const openPick = pickQuestionnaire ? () => setView({ name: "pick" }) : null;
  const pick = async (scope: PickScope) => {
    if (!pickQuestionnaire) return;
    setBusy(true);
    setError(null);
    setPickScope(scope);
    try {
      const result = await pickItems({
        questionnaireId: pickQuestionnaire.id,
        scope,
        answers: answersBody(pickQuestionnaire, pickAnswers),
      });
      setView({ name: "pickResult", scope, result });
    } catch (e) {
      if (isDeadSessionError(e)) setFailed(true);
      else setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  // «Ничего не подошло»: ответы подбора становятся запросом клиента, на итогах — отказом с заполненной анкетой.
  const saveDemand = () => {
    if (!pickQuestionnaire) return;
    const items = outcomeItems(currentState?.outcomes ?? []);
    const withReason = items.find((item) =>
      item.refusalReasons.some((reason) => reason.questionnaireId === pickQuestionnaire.id),
    );
    const outcome = withReason ?? items.find((item) => item.questionnaireId === pickQuestionnaire.id) ?? null;
    const reasonId =
      withReason?.refusalReasons.find((reason) => reason.questionnaireId === pickQuestionnaire.id)?.id ?? null;
    setDemand({ summary: answersSummary(pickQuestionnaire, pickAnswers), answers: pickAnswers, outcome, reasonId });
    setView({ name: "main" });
  };

  if (view.name === "pick" && pickQuestionnaire) {
    return (
      <PickScreen
        {...common}
        questionnaire={pickQuestionnaire}
        answers={pickAnswers}
        scope={pickScope}
        onChange={setPickAnswers}
        onScope={setPickScope}
        onBack={() => setView({ name: "main" })}
        onPick={() => void pick(pickScope)}
      />
    );
  }

  if (view.name === "pickResult" && pickQuestionnaire) {
    return (
      <PickResultScreen
        {...common}
        summary={answersSummary(pickQuestionnaire, pickAnswers)}
        scope={view.scope}
        result={view.result}
        canDemand={inConsultation}
        onBack={() => setView({ name: "pick" })}
        onNetwork={() => void pick("network")}
        onDemand={saveDemand}
      />
    );
  }

  if (view.name === "stats") {
    return (
      <StatsScreen
        {...common}
        shiftOpen={Boolean(status.shift)}
        onBack={() => setView({ name: "main" })}
        onClose={() => run(() => closeShift())}
      />
    );
  }

  if (!status.shift || !status.current) {
    return (
      <ClosedScreen {...common} onOpen={() => run(() => openShift())} onStats={() => setView({ name: "stats" })} />
    );
  }

  if (status.current.isBase) {
    return (
      <HallScreen
        {...common}
        shiftOpenedAt={status.shift.openedAt}
        baseName={status.current.stateName}
        states={states.filter((state) => !state.isBase)}
        onEnter={(stateId) => void enter(stateId)}
        onStats={() => setView({ name: "stats" })}
        onPickItems={openPick}
      />
    );
  }

  const outcomes = currentState?.outcomes ?? [];
  const startedAt = status.current.startedAt;
  const stateName = status.current.stateName;

  if (view.name === "outcomes") {
    return (
      <OutcomesScreen
        {...common}
        stateName={stateName}
        startedAt={startedAt}
        outcomes={outcomes}
        scans={scans}
        demand={demand}
        {...draftProps}
        onRemoveDraft={(key) => setDrafts(drafts.filter((draft) => draft.key !== key))}
        onBack={() => setView({ name: "main" })}
        // Каждый исход ведёт на шаг 2 — там «Ещё результат» и «Завершить», даже если заполнять нечего.
        onPick={(outcome) => setView(outcome.document ? { name: "documents", outcome } : { name: "details", outcome })}
        onPickDemand={(outcome, prefill) => setView({ name: "details", outcome, prefill })}
      />
    );
  }

  if (view.name === "documents") {
    return (
      <DocumentsScreen
        {...common}
        outcome={view.outcome}
        {...draftProps}
        onBack={() => setView({ name: "outcomes" })}
      />
    );
  }

  if (view.name === "details") {
    return (
      <DetailsScreen
        {...common}
        outcome={view.outcome}
        questionnaires={config.questionnaires ?? []}
        prefill={view.prefill}
        {...draftProps}
        onBack={() => setView({ name: "outcomes" })}
      />
    );
  }

  return (
    <>
      <InStateScreen
        {...common}
        stateName={stateName}
        icon={currentState?.icon ?? null}
        color={currentState?.color ?? null}
        startedAt={startedAt}
        hasOutcomes={hasOutcomes}
        scans={inConsultation ? scans : null}
        demand={demand?.summary ?? null}
        onPhone={() => setPrompt("phone")}
        onPickItems={inConsultation ? openPick : null}
        onFinish={() => (hasOutcomes ? setView({ name: "outcomes" }) : void finish())}
      />
      {prompt === "phone" && (
        <PhoneSheet onSubmit={async (phone) => setScans(await sendScan({ phone }))} onClose={() => setPrompt(null)} />
      )}
      {prompt === "customer" && (
        <Sheet onClose={() => setPrompt(null)}>
          <h2 className="text-xl font-semibold">Кто клиент?</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Отсканируйте его карту тегом «Сканер» на главной или введите телефон.
          </p>
          <Button size="lg" onClick={() => setPrompt("phone")}>
            <Icon name="phone" className="size-5" />
            Ввести телефон
          </Button>
          <Button variant="ghost" className="self-center" onClick={() => setPrompt(null)}>
            Пропустить
          </Button>
        </Sheet>
      )}
      {prompt === "fitting" && (
        <Sheet onClose={() => setPrompt(null)}>
          <h2 className="text-xl font-semibold">Клиент примеряет?</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Отсканируйте бирки изделий тегом «Сканер» на главной — они попадут в консультацию сами.
          </p>
          <Button size="lg" onClick={() => setPrompt(null)}>
            Понятно
          </Button>
        </Sheet>
      )}
    </>
  );
}
